import {
  AI_POWERED_RAW_SUPPORT_TYPE,
  SupportType,
} from "@/services/ContentsService";
import { PlatformDetail } from "@/store/reducers/contents";
import { Badge } from "@/v2-components/ui/badge";
import { VariantProps } from "class-variance-authority";

export class SupportTypeUtil {
  /**
   * 지원 타입에 따른 Badge variant를 반환합니다.
   */
  public static getBadgeVariant(
    supportedPlatforms: PlatformDetail[],
  ): VariantProps<typeof Badge>["variant"] {
    const supportType = this.getSupportType(supportedPlatforms);
    return ({
      [SupportType.Native]: "native",
      [SupportType.Mod]: "mod",
      [SupportType.OfficialMod]: "officialMod",
      [SupportType.AiPowered]: "ai-powered",
    }[supportType] || "native") as VariantProps<typeof Badge>["variant"];
  }

  /**
   * 지원 타입을 반환합니다.
   * Mod 타입이지만 official이 true인 경우 Official Mod를 반환합니다.
   */
  public static getSupportType(
    supportedPlatforms: PlatformDetail[],
  ): SupportType {
    /**
     * ★**꺼진 행을 빼고 판정한다** (#5708 R1 s1).
     *
     * 아래 판정이 `every()` 라서 **꺼진 행 하나가 결과를 뒤집는다.** admin 이 끈
     * 플랫폼은 목록·필터·SEO 에서 이미 사라지는데 배지만 그걸 계속 세고 있었다.
     *
     * ```
     *   [steam(AIPowered) 켜짐, quest(Native) 꺼짐]
     *     거르기 전  Native        ← 꺼진 행이 every 를 깬다
     *     거른 후    AI Powered
     * ```
     *
     * ★**호출처가 아니라 여기서 거른다.** 이 계열 호출처는 21곳/11파일이고, 같은
     * 도메인의 형제들이 이미 안에서 거른다 — `ContentsService.overwolfSupported` ·
     * `simracingSupported` · `getTempUnavailablePlatforms`. 「가드는 화면이 아니라
     * 공유 코드에 둔다」(`docs/pitfalls.md` #27 UI 표면 갈아치우기)와 같은 방향이다.
     *
     * ⚠️ 빈 배열이 되면 `allAreAi` 는 `length > 0` 로 막히지만 `allAreMod` 는
     * **`[].every()` 가 참**이라 `Mod` 로 떨어진다. 그건 필터 전과 같은 성질이라
     * (원래도 빈 배열이면 그랬다) 여기서 바꾸지 않는다 — 가드가 그 케이스를 고정한다.
     */
    const platforms = supportedPlatforms.filter((p) => p.enabled);

    const allAreAi =
      platforms.length > 0 &&
      platforms.every((p) => p.supportType === AI_POWERED_RAW_SUPPORT_TYPE);
    if (allAreAi) {
      return SupportType.AiPowered;
    }

    const allAreMod = platforms.every((p) => p.supportType === SupportType.Mod);
    const anyUnofficial = platforms.some((p) => !p.official);

    if (allAreMod) {
      return anyUnofficial ? SupportType.Mod : SupportType.OfficialMod;
    }

    return SupportType.Native;
  }

  /**
   * Badge에 표시할 텍스트를 반환합니다.
   */
  public static getBadgeText(supportedPlatforms: PlatformDetail[]): string {
    return this.getSupportType(supportedPlatforms);
  }
}
